import { ArrowRight, Box, Caption, Divider, IconButton } from "@bcc-ui/ui-components";
import { t } from "i18next";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { AssetFolderKebabMenu } from "./asset-folder-kebab-menu/AssetFolderKebabMenu";
import style from "./assets-sub-menu.scss";
import { findActiveFolder, findViewByUuid, getFolderIcon } from "./assetSubMenuUtils";
import { AssetsCreateNewButton } from "./create-new-button/AssetsCreateNewButton";
import AssetViewKebabMenu from "components/asset-management-system/asset-view-kebab-menu/AssetViewKebabMenu";
import { File } from "components/icons/File";
import { useIsChildRouteEnabled } from "components/navigation/hooks/useIsChildRouteEnabled";
import { useIsRouteEnabled } from "components/navigation/hooks/useIsRouteEnabled";
import { NavigationSubMenuItem } from "components/navigation/navigation-panel/sub-navigation-panel/contents/navigation-sub-menu-item/NavigationSubMenuItem";
import { NavigationDivider } from "components/navigation/navigation-panel/sub-navigation-panel/ui-components/navigation-divider/NavigationDivider";
import { NavigationLabel } from "components/navigation/navigation-panel/sub-navigation-panel/ui-components/navigation-label/NavigationLabel";
import { ASSET_OVERVIEW_ROUTE, ASSET_VIEWS_ROUTE, RouteDefinition } from "components/router/Routes";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";

import testIds from "testIds.json";

interface Props {
    mainRoute: RouteDefinition;
    enteredFromMainNavigation: boolean;
    onKebabMenuOpenChange?: (isOpen: boolean) => void;
}

const lastScrollPosition = { topPixels: 0 };

export const AssetsSubmenu = (props: Props) => {
    const { pathname } = useLocation();
    const history = useHistory();
    const views = useSelector((state: StoreState) => state.assetViewsReducer.views);
    const isRouteEnabled = useIsRouteEnabled();
    const { isChildRouteEnabled, isChildRouteAdvertised } = useIsChildRouteEnabled();

    const user = useSelector((state: StoreState) => state.userReducer.user);
    const isAllowedToViewCustomViews = userSessionService.permittedToViewCustomViews("assets", user);
    const isAllowedToCreateCustomViews = userSessionService.permittedToCreateCustomViews("assets", user);

    const [expandedFolderPaths, setExpandedFolderPaths] = React.useState<ReadonlySet<string>>(new Set());
    const [previousNavigationState, setPreviousNavigationState] = React.useState<{
        pathname?: string;
        views?: typeof views;
    }>({});

    const [isKebabMenuOpen, setIsKebabMenuOpen] = React.useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        props.onKebabMenuOpenChange?.(isKebabMenuOpen);
    }, [isKebabMenuOpen]);

    useEffect(() => {
        if (props.enteredFromMainNavigation) {
            lastScrollPosition.topPixels = 0;
        }
        scrollContainerRef.current?.scrollTo({ top: lastScrollPosition.topPixels });
    }, [props.enteredFromMainNavigation]);

    const toggleExpandedFolder = (path: string) => {
        setExpandedFolderPaths((previousPaths) => {
            const updatedPaths = new Set(previousPaths);

            if (updatedPaths.has(path)) {
                updatedPaths.delete(path);
            } else {
                updatedPaths.add(path);
            }

            return updatedPaths;
        });
    };

    const renderCustomViewRoute = (route: RouteDefinition) => {
        const isFolder = route.canHaveChildren;
        const isEmptyFolder = isFolder && route.childRoutes.length === 0;
        const isArchiveFolder = route.isArchived;

        if (!isChildRouteEnabled(route)) {
            return null;
        }

        if (isFolder) {
            const isFolderExpanded = expandedFolderPaths.has(route.path);

            return (
                <React.Fragment key={route.path}>
                    <NavigationSubMenuItem
                        key={route.path}
                        asBox
                        leftSection={
                            <Box className={style.xxxxsGap}>
                                {!isEmptyFolder && (
                                    <IconButton
                                        size="Sm"
                                        aria-expanded={isFolderExpanded}
                                        onClick={() => toggleExpandedFolder(route.path)}
                                        className={style.iconButton}
                                        testId={testIds.navigation.assets.assetViews.expandFolderButton}
                                    >
                                        <ArrowRight
                                            rotateDown={isFolderExpanded}
                                            color={"--app-subNavigationPanel-iconColor"}
                                            size={16}
                                        />
                                    </IconButton>
                                )}
                                {getFolderIcon(isEmptyFolder, isArchiveFolder, isFolderExpanded)}
                            </Box>
                        }
                        rightSection={
                            <Box className={style.xxxsGap}>
                                {isEmptyFolder && <Caption greyTextStyle>{t("Common.empty")}</Caption>}

                                {isAllowedToCreateCustomViews && !isArchiveFolder && (
                                    <AssetFolderKebabMenu route={route} onDropdownOpenChange={setIsKebabMenuOpen} />
                                )}
                            </Box>
                        }
                        to={""}
                    >
                        {t(route.title)}
                    </NavigationSubMenuItem>

                    {isFolderExpanded &&
                        route.childRoutes.map((nestedRoute) => {
                            if (!isChildRouteEnabled(nestedRoute)) {
                                return null;
                            }

                            const viewDetails =
                                views && nestedRoute.viewUuid ? findViewByUuid(views, nestedRoute.viewUuid) : undefined;

                            return (
                                <NavigationSubMenuItem
                                    key={nestedRoute.path}
                                    to={nestedRoute.path}
                                    leftSection={
                                        isArchiveFolder && (
                                            <File size={20} color={"var(--app-subNavigationPanel-iconColor)"} />
                                        )
                                    }
                                    rightSection={
                                        viewDetails && (
                                            <AssetViewKebabMenu
                                                name={route.title}
                                                viewDetails={viewDetails}
                                                onViewDelete={() => {
                                                    if (pathname === nestedRoute.path) {
                                                        history.replace(ASSET_OVERVIEW_ROUTE.path);
                                                    }
                                                }}
                                                isKebabOpened={setIsKebabMenuOpen}
                                                isOpenedFromNavigation
                                            />
                                        )
                                    }
                                    isAdvertised={isChildRouteAdvertised(nestedRoute)}
                                    className={!isArchiveFolder ? style.nestedFile : undefined}
                                    testId={testIds.navigation.assets.assetViews.customViewLink}
                                >
                                    {t(nestedRoute.title)}
                                </NavigationSubMenuItem>
                            );
                        })}
                </React.Fragment>
            );
        }

        const viewDetails = views && route.viewUuid ? findViewByUuid(views, route.viewUuid) : undefined;

        return (
            <NavigationSubMenuItem
                key={route.path}
                to={route.path}
                leftSection={<File size={20} color={"var(--app-subNavigationPanel-iconColor)"} />}
                rightSection={
                    viewDetails && (
                        <AssetViewKebabMenu
                            name={route.title}
                            viewDetails={viewDetails}
                            onViewDelete={() => {
                                if (pathname === route.path) {
                                    history.replace(ASSET_OVERVIEW_ROUTE.path);
                                }
                            }}
                            isKebabOpened={setIsKebabMenuOpen}
                            isOpenedFromNavigation
                        />
                    )
                }
                isAdvertised={isChildRouteAdvertised(route)}
                className={style.file}
                testId={testIds.navigation.assets.assetViews.customViewLink}
            >
                {t(route.title)}
            </NavigationSubMenuItem>
        );
    };

    if (!props.mainRoute || !isRouteEnabled(props.mainRoute)) {
        return null;
    }

    if (pathname !== previousNavigationState.pathname || views !== previousNavigationState.views) {
        setPreviousNavigationState({ pathname, views });

        const activeFolder = findActiveFolder(pathname);

        if (activeFolder && !expandedFolderPaths.has(activeFolder.path)) {
            const updatedExpandedFolderPaths = new Set(expandedFolderPaths).add(activeFolder.path);

            setExpandedFolderPaths(updatedExpandedFolderPaths);
        }
    }

    return (
        <Box columnLayout className={style.container}>
            {props.mainRoute.childRoutes.map((route) => {
                if (!isChildRouteEnabled(route)) {
                    return null;
                }

                if (route === ASSET_VIEWS_ROUTE && isAllowedToViewCustomViews) {
                    const archiveFolder = route.childRoutes.filter((route) => route.isArchived);
                    const customViews = route.childRoutes.filter((route) => !route.isArchived);

                    return (
                        <React.Fragment key={route.path}>
                            <NavigationDivider />
                            <NavigationLabel caption>{t("AssetManagement.customViews")}</NavigationLabel>

                            <Box
                                columnLayout
                                overflowY="Auto"
                                className={style.scrollContainer}
                                ref={scrollContainerRef}
                                onScroll={(e) => (lastScrollPosition.topPixels = e.currentTarget.scrollTop)}
                            >
                                {customViews.length > 0 ? (
                                    customViews.map(renderCustomViewRoute)
                                ) : (
                                    <Box paddingLeft={"Sm"} paddingRight={"Sm"} paddingBottom={"Xxs"}>
                                        <Caption>{t("AssetManagement.customViewsEmptyMessage")}</Caption>
                                    </Box>
                                )}
                            </Box>

                            <Box columnLayout className={style.footer}>
                                {isAllowedToCreateCustomViews && (
                                    <>
                                        <NavigationDivider />
                                        <AssetsCreateNewButton />
                                        <Divider />
                                    </>
                                )}

                                {archiveFolder.length > 0 && (
                                    <Box columnLayout className={style.archiveFolderContainer}>
                                        {archiveFolder.map(renderCustomViewRoute)}
                                    </Box>
                                )}
                            </Box>
                        </React.Fragment>
                    );
                }

                return (
                    <NavigationSubMenuItem key={route.path} to={route.path} testId={route.testId}>
                        {t(route.title)}
                    </NavigationSubMenuItem>
                );
            })}
        </Box>
    );
};
